import {
	ChangeDetectionStrategy,
	Component,
	HostBinding,
	TemplateRef,
	computed,
	input,
	output,
	signal
} from '@angular/core';
import { ButtonMode, IButton } from '../buttons/index.js';
import { CoreComponent } from '../core/core.component.js';
import { ISingleSeries } from '../graphs/index.js';
import { IconType } from '../icons/index.js';
import { IImage } from '../ui-models.js';

export type CardImageLocation = 'default' | 'end' | 'below';
export type CardDesign = 'default' | 'selection' | 'clean' | 'primaryLight';
export type CardTitleSize = 'h4' | 'h3' | 'h2' | 'h1';

export type CardImage = {
	readonly title: string | undefined;
	readonly image: IImage;
};

export type CardPieGraph = {
	readonly singleSeries: readonly ISingleSeries[];
	readonly colorScheme?: readonly string[];
};

@Component({
	selector: 'lib-card',
	templateUrl: './card.component.html',
	styleUrls: ['./card.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class CardComponent extends CoreComponent {
	public readonly images = input<readonly CardImage[]>();
	public readonly roundCardImages = input<boolean>(this.roundImages());
	public readonly imagesLocation = input<CardImageLocation>('default');
	public readonly title = input<string>();
	public readonly titleTemplate = input<TemplateRef<any>>();
	public readonly subtitle = input<string>();
	public readonly titleSuffix = input<string | number>();
	public readonly titlePrefix = input<string | number>();
	public readonly titleSize = input<CardTitleSize>('h3');
	public readonly avatar = input<IImage>();
	public readonly avatarName = input<string>();
	public readonly avatarWidthAndHeight = input<number>(50);
	public readonly cardIcon = input<IconType>();
	public readonly isForm = input<boolean>(false);
	public readonly isCollapsible = input<boolean>(false);
	public readonly isInDialog = input<boolean>(false);
	public readonly removeContent = input<boolean>(false);
	public readonly removePadding = input<boolean>(false);
	public readonly imagePriority = input<boolean>(false);
	public readonly addContentDivider = input<boolean>(false);
	public readonly enableCard = input<boolean>(true);
	public readonly removeBottomPadding = input<boolean>();
	public readonly pieGraph = input<CardPieGraph>();
	public readonly buttons = input<readonly IButton[]>();
	public readonly buttonsMode = input<ButtonMode>('single');
	public readonly buttonsExtendedListThreshold = input<number>();
	public readonly disableContentMargin = input<boolean>();
	public readonly cardDesign = input<CardDesign>('default');
	public readonly isEditableAvatar = input<boolean>(false);
	public readonly buttonsMoreIcon = input<IconType>('more');

	public readonly editAvatarClick = output<void>();

	@HostBinding('style') override style = 'display: block';

	public readonly isCollapsed = signal<boolean>(false);

	public readonly calculatedAvatarWidthAndHeight = computed<number>(() => {
		if (this.isMobileView()) {
			return 40;
		}

		return this.avatarWidthAndHeight();
	});

	public readonly showCardToolbar = computed<boolean>(() => {
		if (this.title() || this.titleTemplate()) {
			return true;
		}
		return false;
	});

	public readonly addCardToolbarBottomMargin = computed<boolean>(() => {
		if (this.buttons()?.length) {
			return false;
		}
		if (this.isCollapsed()) {
			return false;
		}
		return true;
	});

	public readonly addCardToolbarMargin = computed<boolean>(() => {
		if (this.disableContentMargin()) {
			return false;
		}
		if (this.title() || this.titleTemplate()) {
			return true;
		}

		return false;
	});

	constructor() {
		super();
	}

	handleCollapsed(event: Event, type: 'card' | 'title'): void {
		if (!this.isCollapsible()) {
			return;
		}
		event.stopPropagation();
		event.preventDefault();

		if (type === 'card') {
			if (this.isCollapsed()) {
				this.isCollapsed.update((value) => !value);
			}
		} else {
			this.isCollapsed.update((value) => !value);
		}
	}

	handleAvatarClick(): void {
		if (this.isEditableAvatar()) {
			this.editAvatarClick.emit();
		}
	}
}
